import { useCallback, useEffect, useMemo, useState } from 'react';
import { applyChange, effectiveValue, pruneChanges } from './changeMap';

export interface ChangeMapState<V> {
  /** Vorgemerkter Wert, sonst Ausgangswert */
  get: (key: string) => V | undefined;
  set: (key: string, value: V) => void;
  isChanged: (key: string) => boolean;
  /** Vorgemerkte Änderungen für den Schreibaufruf */
  changes: Array<[string, V]>;
  count: number;
  dirty: boolean;
  reset: () => void;
}

/**
 * Vorgemerkte Änderungen einer Matrix für „Speichern (n)" (Regel „Dokument-Navigation"
 * R5): Haken ändern schreibt nicht, erst „Speichern".
 *
 * `baseline` muss stabil sein (useCallback), es liefert den geladenen Wert je Schlüssel.
 */
export function useChangeMap<V>(baseline: (key: string) => V | undefined): ChangeMapState<V> {
  const [map, setMap] = useState<Map<string, V>>(() => new Map());

  // Neu geladener Stand: Änderungen, die er schon enthält, entfallen.
  useEffect(() => {
    setMap((prev) => {
      const next = pruneChanges(prev, baseline);
      return next.size === prev.size ? prev : next;
    });
  }, [baseline]);

  const get = useCallback((key: string) => effectiveValue(map, baseline, key), [map, baseline]);
  const set = useCallback((key: string, value: V) => setMap((prev) => applyChange(prev, baseline, key, value)), [baseline]);
  const isChanged = useCallback((key: string) => map.has(key), [map]);
  const reset = useCallback(() => setMap(new Map()), []);
  const changes = useMemo(() => [...map.entries()], [map]);

  return { get, set, isChanged, changes, count: map.size, dirty: map.size > 0, reset };
}
